import React, { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { callLLM } from '../lib/assistant/llmService';
import { useAuth } from '../contexts/AuthContext';
import { loadConversation, saveConversation, clearConversation, ConversationMessage } from '../lib/assistant/conversationService';
import { ChatLayout } from '../components/assistant/ChatLayout';
import { ChatHeader } from '../components/assistant/ChatHeader';
import { MessageList } from '../components/assistant/MessageList';
import { Composer } from '../components/assistant/Composer';
import { Sidebar } from '../components/assistant/Sidebar';
import { useBodyLock } from '../hooks/useBodyLock';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const AssistantPage: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lock body scroll when mobile sidebar is open
  useBodyLock(sidebarOpen && isMobile);

  // Charger l'historique au montage du composant
  useEffect(() => {
    const loadHistory = async () => {
      if (!user) {
        setIsLoadingHistory(false);
        return;
      }

      try {
        const savedMessages = await loadConversation(user.id);
        if (savedMessages.length > 0) {
          const convertedMessages: Message[] = savedMessages.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp instanceof Date 
              ? msg.timestamp 
              : new Date(msg.timestamp)
          }));
          setMessages(convertedMessages);
        }
      } catch (error) {
        console.error('Error loading conversation history:', error);
        // Ne pas bloquer le rendu en cas d'erreur - continuer avec messages vides
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [user]);

  // Sauvegarder l'historique après chaque changement
  useEffect(() => {
    if (!user || messages.length === 0 || isLoadingHistory) return;

    const timeoutId = setTimeout(() => {
      const messagesToSave: ConversationMessage[] = messages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp.toISOString()
      }));

      saveConversation(user.id, messagesToSave).catch(error => {
        console.error('Error saving conversation:', error);
      });
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [messages, user, isLoadingHistory]);


  const quickQuestions = [
    'How can I manage clients efficiently?',
    'What is the difference between Lunch, Boost and Scale plans?',
    'How do I create a professional invoice?',
    'How can I optimize my freelance workflow?',
    'How does the referral system work?',
    'How do I track my revenue and stats?'
  ];

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading || !user) return;
    await handleSendMessageWithText(input.trim());
  };


  const handleSuggestionClick = async (suggestion: string) => {
    // Définir l'input et envoyer automatiquement le message
    setInput(suggestion);
    
    // Attendre un tick pour que l'input soit mis à jour, puis envoyer
    setTimeout(() => {
      handleSendMessageWithText(suggestion);
    }, 0);
  };

  const handleSendMessageWithText = async (text: string) => {
    if (!text.trim() || !user) {
      console.warn('Cannot send message:', { text: text.trim(), hasUser: !!user, isLoading });
      return;
    }

    // Empêcher l'envoi si déjà en cours
    if (isLoading) {
      console.warn('Message already being sent, ignoring...');
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date()
    };

    // Ajouter le message utilisateur immédiatement
    setMessages(prev => [...prev, userMessage]);
    const currentMessage = text.trim();
    
    // Nettoyer l'input seulement si c'est le même texte
    if (input.trim() === currentMessage) {
      setInput('');
    }
    
    setIsLoading(true);

    try {
      // Utiliser les messages actuels + le nouveau message utilisateur
      const conversationHistory = [...messages, userMessage].map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content
      }));

      console.log('📤 Sending message to AI:', { currentMessage, historyLength: conversationHistory.length });

      const response = await callLLM(user, currentMessage, conversationHistory);
      
      console.log('📥 Received response from AI:', { responseLength: response.text?.length });
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.text,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error('❌ Assistant error:', error);
      let errorText = 'Sorry, something went wrong. Please try again.';
      
      if (error?.message) {
        if (error.message.includes('401') || error.message.includes('unauthorized') || error.message.includes('invalid')) {
          errorText = '❌ Erreur: Clé OpenAI invalide. Configurez OPENAI_API_KEY dans Supabase Dashboard → Edge Functions → assistant-message → Secrets';
        } else if (error.message.includes('429') || error.message.includes('rate limit')) {
          errorText = '❌ Erreur: Limite de requêtes atteinte. Veuillez attendre 1-2 minutes avant de réessayer.';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorText = '❌ Erreur: Problème de connexion. Vérifiez votre connexion internet.';
        } else {
          errorText = `❌ Erreur: ${error.message}`;
        }
      }
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorText,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetConversation = async () => {
    if (user) {
      await clearConversation(user.id);
    }
    setMessages([]);
  };

  const hasMessages = messages.length > 0;

  // Debug: vérifier que le Composer est bien passé
  console.log('🔍 AssistantPage render:', {
    hasUser: !!user,
    input,
    isLoading,
    isLoadingHistory,
    messagesCount: messages.length,
    composerProps: {
      value: input,
      disabled: !user,
      isLoading,
    }
  });

  return (
    <div 
      className="fixed inset-0 top-[var(--header-h)] flex flex-col lg:flex-row"
      style={{ 
        height: 'calc(100vh - var(--header-h))',
      }}
    >
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        quickQuestions={quickQuestions}
        onSuggestionClick={handleSuggestionClick}
        onNewChat={resetConversation}
        isMobile={isMobile}
      />

      {/* Main Chat Layout - Style ChatGPT */}
      <div 
        className="flex-1 flex flex-col overflow-hidden lg:ml-64"
        style={{
          height: '100%',
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ChatLayout
          header={
            <ChatHeader
              onMenuClick={() => setSidebarOpen(true)}
              onClearClick={resetConversation}
              hasMessages={hasMessages}
              isMobile={isMobile}
            />
          }
          composer={
            <Composer
              value={input}
              onChange={setInput}
              onSend={handleSendMessage}
              isLoading={isLoading}
              disabled={!user}
              placeholder="Ask me anything..."
            />
          }
        >
          <MessageList
            messages={messages}
            isLoading={isLoading}
            isLoadingHistory={isLoadingHistory}
            onScrollToBottom={() => {}}
            quickQuestions={quickQuestions}
            onSuggestionClick={handleSuggestionClick}
          />
        </ChatLayout>
      </div>
    </div>
  );
};

export default AssistantPage;
